import type { AfterViewInit, OnInit, QueryList, TemplateRef } from '@angular/core';
import type { PageEvent } from '@angular/material/paginator';
import type { Sort } from '@angular/material/sort';
import type { ColumnDef, FilterOption, PaginationOption, RowItem, SortOption } from '@commons/bo-ui/table/table.model';

import { animate, state, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ContentChildren,
  EventEmitter,
  inject,
  Input,
  Output,
} from '@angular/core';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatTableModule } from '@angular/material/table';
import {
  BoCellTemplateRefDirective,
  BoExpandableRowTemplateRefDirective,
} from '@commons/bo-ui/table/directives/table.directive';
import { CanExpandPipe } from '@commons/bo-ui/table/pipes/can-expand.pipe';
import { BehaviorSubject } from 'rxjs';

@Component({
  selector: 'app-bo-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('expansion', [
      state('collapsed', style({ height: '0', maxHeight: '0', minHeight: '0' })),
      state('expanded', style({ height: '*', maxHeight: '*', minHeight: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
  standalone: true,
  imports: [MatTableModule, CommonModule, CanExpandPipe, MatMenuModule, MatPaginatorModule],
})
export class BoTableComponent implements AfterViewInit, OnInit {
  @Input() columnsDefinition: ColumnDef[] = [];
  @Input() items: RowItem[] = [];
  @Input() showEmptyHeaderRow = true;
  @Input() emptyListLabel?: string = 'La liste ne contient aucun élément.';
  @Input() rowCustomClass?: string | string[];

  @Input() isExpandable = false;
  @Input() canExpandFn: (row: RowItem) => boolean = () => true;
  @Input() rowExpander$ = new BehaviorSubject<RowItem | null>(null);

  @Input() sort?: SortOption;
  @Input() pagination?: PaginationOption;

  @Output() itemClicked = new EventEmitter<any>();
  @Output() itemCtrlClicked = new EventEmitter<any>();
  @Output() sortChanged = new EventEmitter<Sort>();
  @Output() filtersChanged = new EventEmitter<FilterOption[]>();
  @Output() paginationChanged = new EventEmitter<PaginationOption>();

  @ContentChildren(BoCellTemplateRefDirective)
  cellTemplatesQuery!: QueryList<BoCellTemplateRefDirective>;

  @ContentChild(BoExpandableRowTemplateRefDirective)
  expandableRowTemplate!: BoExpandableRowTemplateRefDirective;

  #cdr = inject(ChangeDetectorRef);

  cellTemplatesMap: Record<string, TemplateRef<any>> = {};
  currentFilters!: Record<string, string>;

  get columnNames(): string[] {
    return this.columnsDefinition.map((c) => c.fieldName);
  }

  get rowExpanded(): RowItem | null {
    return this.rowExpander$.value;
  }

  ngOnInit(): void {
    this.currentFilters = {};
    this.columnsDefinition.filter((c) => c.filters).forEach((c) => (this.currentFilters[c.fieldName] = ''));
  }

  ngAfterViewInit(): void {
    this.buildCellTemplateMap();
    this.#cdr.detectChanges();
  }

  toggleItemExpansion(item: RowItem): void {
    this.rowExpander$.next(this.rowExpanded === item ? null : item);
  }

  buildCellTemplateMap(): void {
    this.cellTemplatesMap = {};
    this.cellTemplatesQuery.forEach((template) => {
      this.cellTemplatesMap[template.id] = template.templateRef;
    });
  }

  handleRowClick(event: MouseEvent, item: RowItem): void {
    if (this.isExpandable && this.canExpandFn && this.canExpandFn(item)) {
      this.toggleItemExpansion(item);
    }

    if (event.ctrlKey || event.metaKey) {
      this.itemCtrlClicked.emit(item);
    } else {
      this.itemClicked.emit(item);
    }
  }

  handleSort(column: string): void {
    const direction = this.sort?.property === column ? (this.sort?.order === 'asc' ? 'desc' : 'asc') : 'asc';
    this.sortChanged.emit({ active: column, direction });
  }

  handleFilter(fieldName: string, filter: string | null): void {
    this.currentFilters[fieldName] = filter!;

    const options: FilterOption[] = [];
    Object.entries(this.currentFilters)
      .filter(([_, value]) => !!value)
      .forEach(([property, value]) => {
        options.push({ property, value });
      });
    this.filtersChanged.emit(options);
  }

  handlePaginationChange(pageEvent: PageEvent): void {
    this.paginationChanged.emit({ currentPage: pageEvent.pageIndex });
  }
}
